import React, { FunctionComponent, useContext } from "react";
import { useTranslate, makePlanDisplayId, makePlanLongId } from "../../helpers";
import {
  PlanType,
  EnrollFormPlanTypeLabels,
  PlanSNPType,
  PlanCategoryType,
  EnrollFormSNPTypeLabelKeys,
  BasePlan,
} from "../../@types";
import { FormattedMessage } from "react-intl-phraseapp";
import { Link } from "react-router-dom";
import routes from "../../app/routes";
import { useFlags } from "launchdarkly-react-client-sdk";
import { getPlanName } from "../../helpers/planHelpers";
import { AppStore } from "../../app/store";

export const getPlanTypeKey = (plan: {
  category: string;
  plan_type: string;
  snp_type: string;
}): string => {
  let planTypeKey = EnrollFormPlanTypeLabels[plan.plan_type];

  if (plan.snp_type === PlanSNPType.SNP_TYPE_DUAL_ELIGIBLE) {
    planTypeKey = EnrollFormPlanTypeLabels[plan.snp_type];
  } else if (
    plan.category === PlanCategoryType.PFFS ||
    plan.category === PlanCategoryType.COST_1876
  ) {
    if (plan.plan_type === PlanType.MA) {
      planTypeKey = EnrollFormPlanTypeLabels[`${plan.category}_MA`];
    } else {
      planTypeKey = EnrollFormPlanTypeLabels[`${plan.category}_PD`];
    }
  }

  return planTypeKey;
};

const PlanInfo: FunctionComponent<{
  plan: BasePlan;
  onlyName?: boolean;
  includeDetailsLink?: boolean;
  showRequiredFieldNote?: boolean;
  useSubmittedText?: boolean;
}> = ({
  plan,
  onlyName,
  includeDetailsLink,
  showRequiredFieldNote = true,
  useSubmittedText = false,
}) => {
  const t = useTranslate();
  const {
    state: { language },
  } = useContext(AppStore.AppContext);
  const { mctCurrentPlanYear } = useFlags();

  let planNameElementKey = "";

  if (useSubmittedText) {
    planNameElementKey = "enroll_form.submitted_plan";
  } else if (plan.contract_year === "2020") {
    planNameElementKey = "enroll_form.chosen_plan";
  } else if (
    mctCurrentPlanYear &&
    mctCurrentPlanYear.toString() !== plan.contract_year
  ) {
    planNameElementKey = "enroll_form.joining_plan_for_year";
  } else {
    planNameElementKey = "enroll_form.joining_plan";
  }

  const getPlanIncludesString = (plan: {
    plan_type: string;
    snp_type: string;
  }): string => {
    if (plan.snp_type !== PlanSNPType.SNP_TYPE_NOT_SNP) {
      return `${t("enroll_form.plan_includes.health_and_drug_coverage")} (${t(
        EnrollFormSNPTypeLabelKeys[plan.snp_type]
      )})`;
    }
    if (plan.plan_type === PlanType.MA) {
      return t("enroll_form.plan_includes.only_health_coverage");
    }
    if (plan.plan_type === PlanType.PDP) {
      return t("enroll_form.plan_includes.only_drug_coverage");
    }
    return t("enroll_form.plan_includes.health_and_drug_coverage");
  };

  const planNameElement = (
    <FormattedMessage
      id={planNameElementKey}
      values={{
        planName: getPlanName(plan, language),
        year: plan.contract_year,
      }}
    />
  );

  return (
    <div className="plan-info">
      {onlyName ? (
        <h2>{getPlanName(plan, language)}</h2>
      ) : (
        <div className="ds-text-heading--xl ds-u-margin-bottom--1">
          {planNameElement}
        </div>
      )}
      <div className="ds-u-color--gray-light">
        <span className="ds-u-font-weight--semibold">
          {t("enroll_form.plan_id")}:
        </span>{" "}
        {makePlanDisplayId(plan)}
        <br />
        {plan.contract_year === "2020" ? (
          <>
            <span className="ds-u-font-weight--semibold">
              {t("enroll_form.plan_type")}:
            </span>{" "}
            {t(getPlanTypeKey(plan))}
          </>
        ) : (
          <>
            <span className="ds-u-font-weight--semibold">
              {t("enroll_form.plan_includes")}:
            </span>{" "}
            {getPlanIncludesString(plan)}
          </>
        )}
      </div>

      {includeDetailsLink && (
        <>
          <br />
          <Link to={routes.planDetails.replace(":id", makePlanLongId(plan))}>
            {t("enroll_form.view_plan_info")}
          </Link>
        </>
      )}

      {plan.contract_year !== "2020" && showRequiredFieldNote && (
        <p className="ds-u-margin-top--4 ds-u-margin-bottom--0">
          {t("enroll_form.required_fields_note")}
        </p>
      )}
    </div>
  );
};

export default PlanInfo;
